import { BillingType, UserRole } from "@next-platform/packages-ts-types";
import { useMemo } from "react";
import { UserRoles } from "../types";
import useUserInfo from "../useUserInfo";
import { RECURRING_MANAGEABLE_PLATFORMS } from "./constants/subscriptionPlan";
import useUserPlanName from "./useUserPlanName";
import { formatSubscriptionDate, getDaysRemaining, getPlanDurationDays, getProgressPercent, isPremiumPlanState, resolveDisplayPlanRole, resolveIsOneTimeExpired, resolveManageDestination, resolvePlanRole, resolvePlanState, } from "./utils/subscriptionPlanDerivations";
const buildSubscriptionInfo = ({ subscription, role, platform, }) => {
    const isOneTime = subscription?.billing_type === BillingType.ONE_TIME;
    const isOneTimeManageable = isOneTime && Boolean(subscription?.platform);
    const expireOn = subscription?.expire_on;
    const formattedExpiryDate = formatSubscriptionDate(expireOn);
    const hasExpiryDate = formattedExpiryDate.length > 0;
    const formattedRenewsDate = formatSubscriptionDate(subscription?.renews_at);
    const hasRenewsDate = formattedRenewsDate.length > 0;
    const daysRemaining = getDaysRemaining(expireOn);
    const totalPlanDays = getPlanDurationDays({ startDate: subscription?.start_date, expireOn });
    const progressPercent = getProgressPercent({ daysRemaining, totalPlanDays });
    const isOneTimeExpired = resolveIsOneTimeExpired({ subscription, isOneTime, daysRemaining });
    const planState = resolvePlanState({ subscription, isOneTime, isOneTimeExpired });
    // Plan identity (title/tier) follows the Kinde-derived User Role; subscription facts stay on the subscription record.
    const planRole = resolvePlanRole(role);
    const subscriptionRole = subscription?.role ?? UserRoles.FREE;
    // When expired, plan identity should reflect the plan that actually expired, not the post-downgrade Kinde role.
    const displayPlanRole = resolveDisplayPlanRole({ planState, planRole, subscriptionRole });
    const manageDestination = resolveManageDestination({ isOneTimeManageable, platform });
    const shouldShowManage = isOneTimeManageable || (platform ? RECURRING_MANAGEABLE_PLATFORMS.has(platform) : false);
    const shouldWarnOnAccountDelete = isPremiumPlanState(planState) && !isOneTime;
    return {
        subscription,
        role,
        planRole,
        subscriptionRole,
        platform,
        formattedExpiryDate,
        hasExpiryDate,
        formattedRenewsDate,
        hasRenewsDate,
        daysRemaining,
        progressPercent,
        planState,
        isOneTime,
        displayPlanRole,
        shouldShowManage,
        shouldWarnOnAccountDelete,
        isOneTimeManageable,
        manageDestination,
    };
};
const useSubscriptionInfo = () => {
    const { subscription, role, platform } = useUserInfo();
    const baseInfo = useMemo(() => buildSubscriptionInfo({ subscription, role, platform }), [platform, role, subscription]);
    const planName = useUserPlanName(baseInfo.planRole);
    const subscriptionPlanName = useUserPlanName(baseInfo.subscriptionRole);
    const displayPlanName = useUserPlanName(baseInfo.displayPlanRole);
    return useMemo(() => ({ ...baseInfo, planName, subscriptionPlanName, displayPlanName }), [baseInfo, displayPlanName, planName, subscriptionPlanName]);
};
export default useSubscriptionInfo;
